import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface LoginMessage {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  category: string;
  icon?: string;
  bgColor: string;
  textColor: string;
  linkUrl?: string;
  linkText?: string;
}

const AUTO_ROTATE_INTERVAL = 6000; // 6 seconds

const LoginMessages: React.FC = () => {
  const [messages, setMessages] = useState<LoginMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
        const response = await fetch(`${apiUrl}/v1/public/login-messages/active`);
        const data = await response.json();
        if (data.success && data.data) {
          setMessages(data.data);
        }
      } catch (error) {
        console.error('Error fetching login messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (messages.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      goToNext();
    }, AUTO_ROTATE_INTERVAL);

    return () => clearInterval(interval);
  }, [messages.length, isPaused, currentIndex]);

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % messages.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [messages.length, isAnimating]);

  const goToPrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + messages.length) % messages.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [messages.length, isAnimating]);

  const goToSlide = useCallback((index: number) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [currentIndex, isAnimating]);

  if (loading || messages.length === 0) {
    return null;
  }

  const currentMessage = messages[currentIndex];

  return (
    <div
      className="w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white/90 text-sm font-medium tracking-wide">
          Dicas & Novidades
        </h3>
        {messages.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrev}
              disabled={isAnimating}
              className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 disabled:opacity-50"
              aria-label="Anterior"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToNext}
              disabled={isAnimating}
              className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 disabled:opacity-50"
              aria-label="Próximo"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Single Card Carousel */}
      <div className="relative overflow-hidden rounded-2xl">
        <div
          className="transition-all duration-500 ease-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          <div className="flex">
            {messages.map((msg, index) => (
              <div
                key={msg.id}
                className="w-full flex-shrink-0 p-5 rounded-2xl"
                style={{
                  backgroundColor: msg.bgColor || '#ffffff',
                  color: msg.textColor || '#1f2937',
                }}
              >
                {/* Icon & Category Badge */}
                <div className="flex items-center gap-2 mb-3">
                  {msg.icon && (
                    <span className="text-2xl">{msg.icon}</span>
                  )}
                  <span
                    className="text-xs px-2.5 py-1 rounded-full font-medium"
                    style={{
                      backgroundColor: `${msg.textColor}15`,
                      color: msg.textColor,
                    }}
                  >
                    {getCategoryLabel(msg.category)}
                  </span>
                </div>

                {/* Title */}
                <h4 className="font-bold text-base mb-1.5">{msg.title}</h4>

                {/* Subtitle */}
                {msg.subtitle && (
                  <p className="text-xs opacity-70 mb-2 font-medium">{msg.subtitle}</p>
                )}

                {/* Content */}
                <p className="text-sm leading-relaxed opacity-90">
                  {msg.content}
                </p>

                {/* Link */}
                {msg.linkUrl && (
                  <div className="mt-4 pt-3 border-t border-current/10">
                    {msg.linkUrl.startsWith('http') ? (
                      <a
                        href={msg.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold hover:opacity-80 inline-flex items-center gap-1.5 transition-opacity"
                        style={{ color: msg.textColor }}
                      >
                        {msg.linkText || 'Saber mais'}
                        <span className="text-lg">→</span>
                      </a>
                    ) : (
                      <Link
                        to={msg.linkUrl}
                        className="text-sm font-semibold hover:opacity-80 inline-flex items-center gap-1.5 transition-opacity"
                        style={{ color: msg.textColor }}
                      >
                        {msg.linkText || 'Saber mais'}
                        <span className="text-lg">→</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dot Indicators */}
      {messages.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {messages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-6 h-2 bg-white'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to get category label
function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    LITERACIA: 'Literacia Financeira',
    IA: 'IA',
    FINTECH: 'Fintech',
    EVENTOS: 'Eventos',
    DOACOES: 'Doações',
    SERVICOS: 'Serviços',
    NOVIDADES: 'Novidades',
    DICAS: 'Dicas',
    PROMOCAO: 'Promoção',
    SEGURANCA: 'Segurança',
  };
  return labels[category] || category;
}

export default LoginMessages;
